// BWF rally-point match state machine (2006+). Pure functions, no UI.

import {
  createIntervalBreak,
  clearIntervalBreak,
  courtChangeOfferedAtInterval,
  intervalOffersCourtChange,
} from "./matchInterval";

export const DEFAULT_POINTS_TO_WIN = 21;
export const DEFAULT_WIN_BY = 2;
export const DEFAULT_CAP = 30;

/** Deuce cap: e.g. 21 → 30, 20 → 29, 15 → 24 (first to cap with one-point lead at cap−1 all). */
export function capForPointsToWin(pointsToWin) {
  const ptw = Math.max(1, Math.min(100, pointsToWin));
  return Math.min(100, ptw + 9);
}

/** @typedef {{ id: string, name: string }} Player */

/**
 * @typedef {Object} SideState
 * @property {number} score
 * @property {Player[]} players
 * @property {string} rightBoxPlayerId — player standing in the right service court (doubles); singles ignores pairing
 */

/**
 * @typedef {Object} Game
 * @property {'singles' | 'doubles'} mode
 * @property {number} pointsToWin
 * @property {number} winBy
 * @property {number} cap
 * @property {SideState} sideA
 * @property {SideState} sideB
 * @property {'A' | 'B'} servingSide
 * @property {string} [servingPlayerId] — active server (singles); persisted for undo/sync
 * @property {'A' | 'B' | null} gameWinner
 * @property {RallyHistoryEntry[]} rallyHistory
 * @property {boolean} intervalAt11Done — mid-game interval at 11 pts applied for this game
 */

/**
 * @typedef {import('./matchInterval').IntervalBreak|null} IntervalBreakState
 */

/**
 * @typedef {Object} RallyHistoryEntry
 * @property {'A' | 'B'} winningSide
 * @property {'A' | 'B'} prevServingSide
 * @property {number} prevScoreA
 * @property {number} prevScoreB
 * @property {string} prevRightBoxA
 * @property {string} prevRightBoxB
 * @property {string|null} prevServingPlayerId
 * @property {'A' | 'B' | null} prevGameWinner
 * @property {boolean} prevIntervalAt11Done
 * @property {boolean} prevEndsSwapped
 * @property {import('./matchInterval').IntervalBreak|null} prevIntervalBreak
 */

/**
 * @typedef {Object} Match
 * @property {1 | 3 | 5} bestOf
 * @property {Game[]} games — completed games
 * @property {Game | null} activeGame
 * @property {'A' | 'B' | null} matchWinner
 * @property {boolean} endsSwapped
 * @property {IntervalBreakState} [intervalBreak]
 * @property {boolean} [awaitingNextGame] — between games: court setup before next game starts
 */

export function gamesToWin(bestOf) {
  return Math.floor(bestOf / 2) + 1;
}

/** Game winner from current scores (cap at 30 from 29–29). */
export function computeGameWinner(
  scoreA,
  scoreB,
  pointsToWin = DEFAULT_POINTS_TO_WIN,
  winBy = DEFAULT_WIN_BY,
  cap = DEFAULT_CAP
) {
  if (scoreA === cap && scoreB === cap - 1) return "A";
  if (scoreB === cap && scoreA === cap - 1) return "B";
  if (scoreA >= pointsToWin && scoreA - scoreB >= winBy) return "A";
  if (scoreB >= pointsToWin && scoreB - scoreA >= winBy) return "B";
  return null;
}

export function createMatch(bestOf) {
  return {
    bestOf,
    games: [],
    activeGame: null,
    matchWinner: null,
    endsSwapped: false,
  };
}

function assertPlayerOnSide(players, playerId, label) {
  if (!players.some((p) => p.id === playerId)) {
    throw new Error(`${label}: player ${playerId} is not on this side`);
  }
}

function mkSide(players, rightBoxPlayerId) {
  assertPlayerOnSide(players, rightBoxPlayerId, "mkSide");
  return { score: 0, players: [...players], rightBoxPlayerId };
}

function cloneSide(side) {
  return { ...side, players: [...side.players] };
}

function getSide(game, side) {
  return side === "A" ? game.sideA : game.sideB;
}

function playerInBox(sideState, mode, box) {
  const right = sideState.players.find((p) => p.id === sideState.rightBoxPlayerId);
  if (mode === "singles") return sideState.players[0];
  const left = sideState.players.find((p) => p.id !== sideState.rightBoxPlayerId);
  return box === "right" ? right : left;
}

function serverBoxForScore(score) {
  return score % 2 === 0 ? "right" : "left";
}

/** BWF service court from the server's current rally score. */
export function serviceCourtForServerScore(score) {
  return serverBoxForScore(score);
}

/**
 * Start a new active game (or replace active game). Scores 0–0; first server in the right box.
 */
export function startGame(
  match,
  mode,
  sideAPlayers,
  sideBPlayers,
  firstServerSide,
  firstServerPlayerId,
  options = {}
) {
  const pointsToWin = options.pointsToWin ?? DEFAULT_POINTS_TO_WIN;
  const winBy = options.winBy ?? DEFAULT_WIN_BY;
  const cap = options.cap ?? capForPointsToWin(pointsToWin);

  if (mode === "singles") {
    if (sideAPlayers.length !== 1 || sideBPlayers.length !== 1) {
      throw new Error("Singles requires exactly one player per side");
    }
  } else if (sideAPlayers.length !== 2 || sideBPlayers.length !== 2) {
    throw new Error("Doubles requires exactly two players per side");
  }

  const serverPlayers = firstServerSide === "A" ? sideAPlayers : sideBPlayers;
  assertPlayerOnSide(serverPlayers, firstServerPlayerId, "firstServer");

  const defaultRightA = sideAPlayers[0].id;
  const defaultRightB = sideBPlayers[0].id;

  const sideA = mkSide(
    sideAPlayers,
    firstServerSide === "A" ? firstServerPlayerId : defaultRightA
  );
  const sideB = mkSide(
    sideBPlayers,
    firstServerSide === "B" ? firstServerPlayerId : defaultRightB
  );

  const activeGame = {
    mode,
    pointsToWin,
    winBy,
    cap,
    sideA,
    sideB,
    servingSide: firstServerSide,
    servingPlayerId: firstServerPlayerId,
    gameWinner: null,
    rallyHistory: [],
    intervalAt11Done: false,
  };

  return { ...match, activeGame, matchWinner: match.matchWinner };
}

export function getServerBox(game) {
  const score = getSide(game, game.servingSide).score;
  return serverBoxForScore(score);
}

/** BWF Law 9: server from serving side's score parity + who stands in each service box. */
export function getCurrentServer(game) {
  if (!game || game.gameWinner) return null;
  const box = getServerBox(game);
  const side = game.servingSide;
  const sideState = getSide(game, side);
  if (game.servingPlayerId) {
    const player = sideState.players.find((p) => p.id === game.servingPlayerId);
    if (player) return { side, player, box };
  }
  const player = playerInBox(sideState, game.mode, box);
  return { side, player, box };
}

export function getCurrentReceiver(game) {
  if (!game || game.gameWinner) return null;
  const serverBox = getServerBox(game);
  const recvBox = serverBox === "right" ? "left" : "right";
  const recvSide = game.servingSide === "A" ? "B" : "A";
  const player = playerInBox(getSide(game, recvSide), game.mode, recvBox);
  return { side: recvSide, player };
}

/** Server's score shown first (e.g. 5–3 when A serves). */
export function getScoreDisplay(game) {
  const a = game.sideA.score;
  const b = game.sideB.score;
  if (game.servingSide === "A") {
    return { display: `${a}-${b}`, serverFirst: a, receiverFirst: b };
  }
  return { display: `${b}-${a}`, serverFirst: b, receiverFirst: a };
}

function swapDoublesBoxes(sideState) {
  if (sideState.players.length < 2) return sideState;
  const other = sideState.players.find((p) => p.id !== sideState.rightBoxPlayerId);
  return { ...sideState, rightBoxPlayerId: other.id };
}

function isDecidingGame(match) {
  const need = gamesToWin(match.bestOf);
  const winsA = match.games.filter((g) => g.gameWinner === "A").length;
  const winsB = match.games.filter((g) => g.gameWinner === "B").length;
  return winsA === need - 1 && winsB === need - 1;
}

/**
 * BWF: winner of previous game serves first in the next game (court setup defaults).
 * @param {Match} match — completed games, no active game
 */
export function suggestCourtSetupAfterGame(match) {
  const lastGame = match?.games?.[match.games.length - 1];
  const gw = lastGame?.gameWinner;
  if (!gw) {
    return {
      nextGameNumber: (match?.games?.length ?? 0) + 1,
      suggestedServerSide: /** @type {'A'|'B'} */ ("A"),
      suggestedServerPlayerId: null,
    };
  }
  const serverAtEnd = getCurrentServer({ ...lastGame, gameWinner: null });
  const sidePlayers = gw === "A" ? lastGame.sideA.players : lastGame.sideB.players;
  const firstServerId =
    (serverAtEnd?.side === gw ? serverAtEnd.player?.id : null) ??
    sidePlayers[0]?.id ??
    null;
  return {
    nextGameNumber: match.games.length + 1,
    suggestedServerSide: gw,
    suggestedServerPlayerId: firstServerId,
  };
}

function wouldWinGame(game, side) {
  const a = game.sideA.score + (side === "A" ? 1 : 0);
  const b = game.sideB.score + (side === "B" ? 1 : 0);
  return computeGameWinner(a, b, game.pointsToWin, game.winBy, game.cap) === side;
}

export function isGamePoint(match) {
  const game = match.activeGame;
  if (!game || game.gameWinner || match.matchWinner) return false;
  return wouldWinGame(game, "A") || wouldWinGame(game, "B");
}

export function isMatchPoint(match) {
  const game = match.activeGame;
  if (!game || game.gameWinner || match.matchWinner) return false;
  const need = gamesToWin(match.bestOf);
  const winsA = match.games.filter((g) => g.gameWinner === "A").length;
  const winsB = match.games.filter((g) => g.gameWinner === "B").length;

  const aWinsGame = wouldWinGame(game, "A");
  const bWinsGame = wouldWinGame(game, "B");
  if (aWinsGame && winsA === need - 1) return true;
  if (bWinsGame && winsB === need - 1) return true;
  return false;
}

/** Whether sides should swap ends (after each completed game; at 11 in deciding game). */
export function shouldSwapEndsAfterGame(match) {
  return true;
}

function matchWinnerFromGames(match) {
  const need = gamesToWin(match.bestOf);
  const winsA = match.games.filter((g) => g.gameWinner === "A").length;
  const winsB = match.games.filter((g) => g.gameWinner === "B").length;
  if (winsA >= need) return "A";
  if (winsB >= need) return "B";
  return null;
}

function applyMidGameInterval(match, game, nextScoreA, nextScoreB) {
  if (game.intervalAt11Done) {
    return {
      match,
      game,
      intervalAt11Done: true,
      triggered: false,
      deciding: false,
    };
  }
  const crossed =
    (game.sideA.score < 11 && nextScoreA >= 11) ||
    (game.sideB.score < 11 && nextScoreB >= 11);
  if (!crossed) {
    return {
      match,
      game,
      intervalAt11Done: false,
      triggered: false,
      deciding: false,
    };
  }
  const deciding = isDecidingGame(match);
  const nextMatch = match;
  return {
    match: nextMatch,
    game: { ...game, intervalAt11Done: true },
    intervalAt11Done: true,
    triggered: true,
    deciding,
  };
}

/**
 * Referee resumes play after a BWF interval break (mid-game, final-game mid, or between games).
 * @param {Match} match
 * @param {{ applyCourtChange?: boolean }} [options]
 * @returns {Match}
 */
export function resumeFromInterval(match, options = {}) {
  const br = match?.intervalBreak;
  if (!br) return match;
  const applyCourtChange = Boolean(options.applyCourtChange);
  let cleared = clearIntervalBreak(match);
  if (
    applyCourtChange &&
    courtChangeOfferedAtInterval(cleared, br)
  ) {
    cleared = { ...cleared, endsSwapped: !cleared.endsSwapped };
  }
  if (br.kind !== "between_games") return cleared;

  const lastGame = cleared.games[cleared.games.length - 1];
  if (!lastGame?.gameWinner) return cleared;

  return { ...cleared, awaitingNextGame: true };
}

/**
 * Record one rally won by winningSide. Returns new match state.
 */
export function recordRally(match, winningSide) {
  if (match?.intervalBreak) return match;

  const game = match.activeGame;
  if (!game || game.gameWinner || match.matchWinner) return match;

  const historyEntry = {
    winningSide,
    prevServingSide: game.servingSide,
    prevServingPlayerId: game.servingPlayerId ?? null,
    prevScoreA: game.sideA.score,
    prevScoreB: game.sideB.score,
    prevRightBoxA: game.sideA.rightBoxPlayerId,
    prevRightBoxB: game.sideB.rightBoxPlayerId,
    prevGameWinner: game.gameWinner,
    prevIntervalAt11Done: game.intervalAt11Done,
    prevEndsSwapped: match.endsSwapped,
    prevIntervalBreak: match.intervalBreak ?? null,
  };

  let sideA = cloneSide(game.sideA);
  let sideB = cloneSide(game.sideB);
  let servingSide = game.servingSide;
  let servingPlayerId = game.servingPlayerId ?? null;
  const servingWon = winningSide === servingSide;

  if (winningSide === "A") sideA = { ...sideA, score: sideA.score + 1 };
  else sideB = { ...sideB, score: sideB.score + 1 };

  if (servingWon) {
    if (game.mode === "doubles") {
      if (winningSide === "A") sideA = swapDoublesBoxes(sideA);
      else sideB = swapDoublesBoxes(sideB);
    }
  } else {
    servingSide = winningSide;
    const recvSideState = winningSide === "A" ? sideA : sideB;
    const srvBox = serverBoxForScore(recvSideState.score);
    if (game.mode === "singles") {
      servingPlayerId = recvSideState.players[0]?.id ?? servingPlayerId;
    } else {
      servingPlayerId =
        playerInBox(recvSideState, game.mode, srvBox)?.id ?? servingPlayerId;
    }
  }

  let nextGame = {
    ...game,
    sideA,
    sideB,
    servingSide,
    servingPlayerId,
    rallyHistory: [...game.rallyHistory, historyEntry],
  };

  let nextMatch = match;

  const interval = applyMidGameInterval(
    nextMatch,
    { ...game, intervalAt11Done: game.intervalAt11Done },
    sideA.score,
    sideB.score
  );
  nextMatch = interval.match;
  nextGame = { ...nextGame, intervalAt11Done: interval.intervalAt11Done };

  if (interval.triggered) {
    const gameNum = match.games.length + 1;
    const midKind = interval.deciding ? "final_mid" : "mid_game";
    nextMatch = {
      ...nextMatch,
      intervalBreak: createIntervalBreak(midKind, {
        gameNumber: gameNum,
        scoreA: sideA.score,
        scoreB: sideB.score,
        showEndsChange: intervalOffersCourtChange(nextMatch, midKind, gameNum),
      }),
    };
  }

  const gw = computeGameWinner(
    sideA.score,
    sideB.score,
    game.pointsToWin,
    game.winBy,
    game.cap
  );
  if (gw) {
    nextGame = { ...nextGame, gameWinner: gw };
    const completedGames = [...match.games, nextGame];
    nextMatch = {
      ...nextMatch,
      games: completedGames,
      activeGame: null,
    };
    const mw = matchWinnerFromGames(nextMatch);
    if (mw) {
      return { ...nextMatch, matchWinner: mw };
    }
    const completedGameNum = completedGames.length;
    return {
      ...nextMatch,
      intervalBreak: createIntervalBreak("between_games", {
        gameNumber: completedGameNum,
        scoreA: sideA.score,
        scoreB: sideB.score,
        showEndsChange: intervalOffersCourtChange(
          { ...nextMatch, games: completedGames },
          "between_games",
          completedGameNum
        ),
      }),
    };
  }

  return { ...nextMatch, activeGame: nextGame };
}

/** Referee override: switch serving side without changing score or box positions. */
export function setServingSide(match, side) {
  const game = match.activeGame;
  if (!game || game.gameWinner || match.matchWinner) return match;
  const sideState = getSide(game, side);
  let servingPlayerId = game.servingPlayerId;
  if (game.mode === "singles" && sideState.players[0]) {
    servingPlayerId = sideState.players[0].id;
  }
  return {
    ...match,
    activeGame: { ...game, servingSide: side, servingPlayerId },
  };
}

export function canUndoRally(match) {
  return (match?.activeGame?.rallyHistory?.length ?? 0) > 0;
}

export function undoLastRally(match) {
  const game = match.activeGame;
  if (!game || game.rallyHistory.length === 0) return match;

  const history = [...game.rallyHistory];
  const last = history.pop();

  const restored = {
    ...game,
    servingSide: last.prevServingSide,
    servingPlayerId:
      last.prevServingPlayerId !== undefined
        ? last.prevServingPlayerId
        : game.servingPlayerId,
    sideA: {
      ...game.sideA,
      score: last.prevScoreA,
      rightBoxPlayerId: last.prevRightBoxA,
    },
    sideB: {
      ...game.sideB,
      score: last.prevScoreB,
      rightBoxPlayerId: last.prevRightBoxB,
    },
    gameWinner: last.prevGameWinner,
    intervalAt11Done: last.prevIntervalAt11Done,
    rallyHistory: history,
  };

  const next = {
    ...match,
    endsSwapped: last.prevEndsSwapped,
    activeGame: restored,
    matchWinner: null,
  };
  if (last.prevIntervalBreak) {
    next.intervalBreak = last.prevIntervalBreak;
  } else {
    delete next.intervalBreak;
  }
  return next;
}
